// App.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "./auth";
import Login from "./pages/Login";
import AdminNav from "./components/nav-bars/AdminNav";
import ClientNav from "./components/nav-bars/ClientNav";
import CandidateNav from "./components/nav-bars/CandidateNav";
import "./App.css";
import ActiveOrders from "./pages/admin-pages/order-pages/AdminReview";
import CurrentOrders from "./pages/client-pages/CurrentOrders";
import NeedsReview from "./pages/candidate-pages/NeedsReview";
import PendingOrders from "./pages/admin-pages/order-pages/PendingOrders";
import Clients from "./pages/admin-pages/Clients";
import Invoicing from "./pages/admin-pages/Invoicing";
import VendorOrders from "./pages/admin-pages/VendorOrders";
import Profile from "./pages/client-pages/Profile";
import PositionCodes from "./pages/client-pages/PositionCodes";
import PastCandidates from "./pages/client-pages/PastCandidates";
import Register from "./pages/Register";
import SimpleNav from "./components/nav-bars/SimpleNav";
import UserAgreement from "./pages/client-pages/registration-pages/UserAgreement";
import KeyInformation from "./pages/client-pages/registration-pages/KeyInformation";
import DefaultPositionCodes from "./pages/client-pages/registration-pages/DefaultPositionCodes";
import AllOrders from "./pages/admin-pages/order-pages/AllOrders";
import ClientViewOrder from "./pages/client-pages/ClientViewOrder";
import ClientRegister from "./pages/ClientRegister";
import CandidateRegister from "./pages/CandidateRegister";
import CriminalRecordsAuthForm from "./pages/candidate-pages/candidate-forms/CriminalRecordsAuthForm";
import BackgroundCheckAgreementForm from "./pages/candidate-pages/candidate-forms/BackgroundCheckAgreementForm";
import CreditReportAuthorizationForm from "./pages/candidate-pages/candidate-forms/CreditReportAuthorizationForm";
import MotorVehicleAuthorizationForm from "./pages/candidate-pages/candidate-forms/MotorVehicleAuthorizationForm";
import ReferenceScreeningAuthForm from "./pages/candidate-pages/candidate-forms/ReferenceScreeningAuthForm";
import CandidateScreeningQuestionnaire from "./pages/candidate-pages/candidate-forms/CandidateScreeningQuestionnaire";
import ReferenceIdentificationForm from "./pages/candidate-pages/candidate-forms/ReferenceIdentificationForm";
import Questionnaires from "./pages/client-pages/Questionnaires";
import AdminLogin from "./pages/AdminLogin";
import Confirmation from "./pages/client-pages/registration-pages/Confirmation";
import AdminReview from "./pages/admin-pages/order-pages/AdminReview";
import CompletedOrders from "./pages/admin-pages/order-pages/CompletedOrders";
import InProgress from "./pages/admin-pages/order-pages/InProgress";
import RequiredForms from "./pages/client-pages/RequiredForms";
import AdminViewOrder from "./pages/admin-pages/AdminViewOrder";
import OrderService from "./pages/admin-pages/OrderService";
import AddReference from "./pages/admin-pages/AddReference";
import CreateOrder from "./pages/admin-pages/CreateOrder";
import ViewClient from "./pages/admin-pages/ViewClient";
import AdminPositionCodes from "./pages/admin-pages/AdminPositionCodes";
import EditQuestionnaires from "./pages/admin-pages/EditQuestionnaires";
import CreateClient from "./pages/admin-pages/CreateClient";
import ChooseDefaultPosCodes from "./pages/client-pages/registration-pages/ChooseDefaultPosCodes";
import DeletedOrders from "./pages/admin-pages/order-pages/DeletedOrders";
import ProcessOrders from "./pages/admin-pages/ProcessOrders";
import ViewVendor from "./pages/admin-pages/ViewVendor";
import ReferenceForm from "./components/questionnaires/ReferenceForm";
import ExpiredTokenPage from "./components/static-pages/ExpiredTokenPage";
import AlreadySubmittedPage from "./components/static-pages/AlreadySubmittedPage";
import ThankYouPage from "./components/static-pages/ThankYouPage";
import ErrorPage from "./components/static-pages/ErrorPagee";
import Bishops from "./pages/admin-pages/Bishops";
import CreateBishop from "./pages/admin-pages/CreateBishop";
import ViewBishop from "./pages/admin-pages/ViewBishop";
import Schools from "./pages/admin-pages/Schools";
import ViewSchool from "./pages/admin-pages/ViewSchool";
import CreateSchool from "./pages/admin-pages/CreateSchool";
import ReferenceNav from "./components/nav-bars/ReferenceNav";
import ReferenceStatuses from "./pages/candidate-pages/ReferenceStatuses";
import SuranForm from "./pages/candidate-pages/candidate-forms/SuranForm";
import ThankYouSuran from "./components/static-pages/ThankYouSuran";
import SuranPayment from "./pages/candidate-pages/SuranPayment";
import TransferReleaseUpload from "./pages/candidate-pages/candidate-forms/TransferReleaseUpload";
import NotaryUpload from "./pages/candidate-pages/candidate-forms/NotaryUpload";
import EmailSending from "./pages/admin-pages/EmailSending";
import PrivacyPolicy from "./components/static-pages/PrivacyPolicy";
import ReferenceTracking from "./pages/admin-pages/ReferenceTracking";
import CloseOrder from "./pages/admin-pages/CloseOrder";
import MergeClients from "./pages/admin-pages/MergeClients";
import EditDefaults from "./pages/admin-pages/EditDefaults";
import { checkForms } from "./services/clientService";
import HelpPage from "./components/static-pages/HelpPage";
import HistoryTables from "./pages/admin-pages/HistoryTables";
import RecentActivity from "./pages/admin-pages/RecentActivity";
import AuthCallback from "./authCallback";
import EditReferenceAuthorization from "./pages/admin-pages/EditReferenceAuthorization";
import { jwtDecode } from "jwt-decode";
import { refreshAdminToken } from "./services/authHelper";
import { msalInstance } from "./index";
import SocialMediaAuthorizationForm from "./pages/candidate-pages/candidate-forms/SocialMediaAuthorizationForm";
import PendingVendorOrders from "./pages/admin-pages/PendingVendorOrders";
import SubscriptionOrders from "./pages/admin-pages/SubscriptionOrders";
import SubscriptionOrdersClient from "./pages/client-pages/SubscriptionOrdersClient";
import SubscriptionDetailCEV from "./pages/admin-pages/SubscriptionDetailCEV";
import ReviewQueue from "./pages/admin-pages/ReviewQueue";

const AppContent = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const { decodeToken } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
  const API_URL = process.env.REACT_APP_API_URL || "";
  const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
  const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

  const apiBaseUrl =
    window.location.hostname === "localhost"
      ? API_URL
      : window.location.hostname === new URL(PROD_APP_URL).hostname
        ? PROD_API_URL
        : DEV_API_URL;

  const location = useLocation();

  const isRegistrationPath = useCallback(() => {
    return (
      location.pathname.startsWith("/client/registration") ||
      location.pathname.startsWith("/candidate/registration")
    );
  }, [location.pathname]);

  const isReferencePath = () => {
    return location.pathname.startsWith("/reference");
  };

  const isSuranPath = () => {
    return location.pathname.startsWith("/suran");
  };

  const handleLogout = useCallback(() => {
    logout();
    setIsAuthenticated(false);
    setUserRole(null);
  }, [logout]);

  useEffect(() => {
    const init = async () => {
      let token = localStorage.getItem("token");
      const portalType = localStorage.getItem("portalType");

      if (token) {
        //expiration check
        try {
          const decodedToken: any = jwtDecode(token);

          if (decodedToken.exp * 1000 < Date.now()) {
            // admin - 24hr
            if (portalType === "admin") {
              try {
                token = await refreshAdminToken(msalInstance);
                if (!token) throw new Error("Token refresh failed");
              } catch (refreshError) {
                console.error("Admin session truly expired.");
                localStorage.clear();
                handleLogout();
                setLoading(false);
                return;
              }
            } else {
              // clients/candidates - 3hr
              localStorage.clear();
              handleLogout();
              setLoading(false);
              return;
            }
          }
        } catch (error) {
          localStorage.clear();
          handleLogout();
          setLoading(false);
          return;
        }

        const email = await decodeToken(token);
        setIsAuthenticated(true);
        setUserRole(portalType);

        try {
          const response = await fetch(`${apiBaseUrl}/api/auth/verify-email`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, portal: portalType }),
          });

          const data = await response.json();
          if (!response.ok) {
            console.error("Portal type validation failed:", data.message);
            handleLogout();
          }

          const clientIDString = localStorage.getItem("clientID");
          const clientID = clientIDString ? parseInt(clientIDString, 10) : 0;
          if (
            portalType === "client" &&
            location.pathname !== "/client/registration/user-agreement"
          ) {
            const [formsOk, missing] = await checkForms(clientID, "client");
            if (!formsOk && missing.includes("4")) {
              navigate("/client/registration/user-agreement", {
                state: { clientID: clientID },
              });
              return;
            }
          }
        } catch (error) {
          console.error("Error validating portal type:", error);
        }
      } else if (isRegistrationPath()) {
        setUserRole("registration");
        setIsSidebarOpen(window.innerWidth > 768);
      }
      setLoading(false);
    };

    init();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (!isMobile) setIsSidebarOpen(true);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    userRole,
    location.pathname,
    decodeToken,
    isMobile,
    apiBaseUrl,
    isRegistrationPath,
  ]);

  if (loading) {
    return <div>Loading...</div>;
  }

  const handleLoginButtonClick = (role: string) => {
    if (isAuthenticated) {
      setUserRole(role);
    }
  };

  return (
    <>
      {isAuthenticated ? (
        <>
          {userRole === "admin" && (
            <AdminNav
              isMobile={isMobile}
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
              handleLogout={handleLogout}
            />
          )}
          {userRole === "client" && (
            <ClientNav
              isMobile={isMobile}
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
              handleLogout={handleLogout}
            />
          )}
          {userRole === "candidate" && (
            <CandidateNav
              isMobile={isMobile}
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
              handleLogout={handleLogout}
            />
          )}
        </>
      ) : (
        <>
          {isRegistrationPath() && (
            <SimpleNav
              isMobile={isMobile}
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
              handleLogout={handleLogout}
            />
          )}
          {isReferencePath() && (
            <ReferenceNav
              isMobile={isMobile}
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
              handleLogout={handleLogout}
            />
          )}
          {isSuranPath() && (
            <ReferenceNav
              isMobile={isMobile}
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
              handleLogout={handleLogout}
            />
          )}
        </>
      )}

      <div
        className={`main-content ${
          isAuthenticated ||
          isRegistrationPath() ||
          isReferencePath() ||
          isSuranPath()
            ? "shifted"
            : ""
        }`}
      >
        <Routes>
          <Route
            path="/"
            element={<Login handleLoginButtonClick={handleLoginButtonClick} />}
          />
          <Route
            path="/login"
            element={<Login handleLoginButtonClick={handleLoginButtonClick} />}
          />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/admin-login"
            element={
              <AdminLogin handleLoginButtonClick={handleLoginButtonClick} />
            }
          />
          <Route path="/registration" element={<Register />} />

          <Route path="/client/registration" element={<ClientRegister />} />
          <Route
            path="/client/registration/user-agreement"
            element={<UserAgreement />}
          />
          <Route
            path="/client/registration/key-information"
            element={<KeyInformation />}
          />
          <Route
            path="/client/registration/choose-default-position-codes"
            element={<ChooseDefaultPosCodes />}
          />
          <Route
            path="/client/registration/position-codes"
            element={<DefaultPositionCodes />}
          />
          <Route
            path="/client/registration/confirmation"
            element={<Confirmation />}
          />

          <Route
            path="/candidate/registration"
            element={<CandidateRegister />}
          />

          <Route
            path="/reference-questionnaire/:token"
            element={<ReferenceForm isEditing={false} />}
          />

          {/* Reference Questionnaire Submit Pages */}
          <Route path="/expired" element={<ExpiredTokenPage />} />
          <Route path="/already-submitted" element={<AlreadySubmittedPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />

          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          <Route path="/help" element={<HelpPage />} />

          <Route
            path="suran/ucc/:suranID"
            element={<SuranForm clientAbbreviation={"784"} />}
          />
          <Route
            path="suran/doc/:suranID"
            element={<SuranForm clientAbbreviation={"785"} />}
          />
          <Route path="suran/payment/:suranID" element={<SuranPayment />} />
          <Route path="suran/thank-you" element={<ThankYouSuran />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <ActiveOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/orders/all"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <AllOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/orders/pending-orders"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <PendingOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/orders/admin-review"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <AdminReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/orders/in-progress"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <InProgress />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/orders/completed-orders"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <CompletedOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/orders/deleted-orders"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <DeletedOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/view-order/:orderId"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <AdminViewOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/order-service/:criminalDetailId?"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <OrderService />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/subscription-detail/:subscriptionDetailId?"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <SubscriptionDetailCEV />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/add-reference/:referenceDetailId?"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <AddReference />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/create-order"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <CreateOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/clients"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <Clients />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/create-client"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <CreateClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/view-client/:clientID"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <ViewClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/view-client/position-codes/:clientID"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <AdminPositionCodes />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/view-client/history/:clientID"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <HistoryTables />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/view-client/reference-authorization/:clientID"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <EditReferenceAuthorization />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/view-client/questionnaire/:clientID"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <EditQuestionnaires />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/reports/reference-tracking"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <ReferenceTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/reports/close-orders"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <CloseOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/invoicing"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <Invoicing />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/invoicing/subscription-orders"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <SubscriptionOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/vendor/pending-vendor-orders"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <PendingVendorOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/vendor/review-queue"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <ReviewQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/vendor/process-orders/:vendorID"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <ProcessOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/vendor/orders"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <VendorOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/view-vendor/:vendorID"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <ViewVendor />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/master-lists/bishops"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <Bishops />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/bishops/view-bishop/:bishopID"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <ViewBishop />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/bishops/create-bishop"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <CreateBishop />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/master-lists/schools"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <Schools />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/schools/view-school/:schoolID"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <ViewSchool />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/schools/create-school"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <CreateSchool />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/email"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <EmailSending />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/merge-clients"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <MergeClients />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/edit-defaults"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <EditDefaults />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/recent-activity"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="admin"
              >
                <RecentActivity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="client"
              >
                <CurrentOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/current-orders"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="client"
              >
                <CurrentOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/profile"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="client"
              >
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/position-codes"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="client"
              >
                <PositionCodes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/questionnaires"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="client"
              >
                <Questionnaires />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/required-forms"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="client"
              >
                <RequiredForms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/past-candidates"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="client"
              >
                <PastCandidates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/view-order/:orderId"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="client"
              >
                <ClientViewOrder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/subscriptions"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="client"
              >
                <SubscriptionOrdersClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <NeedsReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/needs-review"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <NeedsReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/reference-statuses"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <ReferenceStatuses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/past-references"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <NeedsReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/past-background-checks"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <NeedsReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/background-check-auth-fcra"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <BackgroundCheckAgreementForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/criminal-records-auth"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <CriminalRecordsAuthForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/credit-report-auth"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <CreditReportAuthorizationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/motor-vehicle-auth"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <MotorVehicleAuthorizationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/reference-screening-auth"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <ReferenceScreeningAuthForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/reference-identification"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <ReferenceIdentificationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/screening-questionnaire"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <CandidateScreeningQuestionnaire />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/transfer-release"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <TransferReleaseUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/notary"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <NotaryUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate/forms/social-media-auth"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                requiredRole="candidate"
              >
                <SocialMediaAuthorizationForm />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<ErrorPage />} />
        </Routes>
      </div>
    </>
  );
};

// ProtectedRoute component to protect routes based on role
const ProtectedRoute: React.FC<{
  children?: React.ReactNode;
  isAuthenticated: boolean;
  userRole: string | null;
  requiredRole: string | string[];
}> = ({ children, isAuthenticated, userRole, requiredRole }) => {
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (userRole === null) return <div>Loading...</div>;

  const hasAccess = Array.isArray(requiredRole)
    ? requiredRole.includes(userRole)
    : userRole === requiredRole;

  return hasAccess ? <>{children}</> : <Navigate to="/error/403" replace />;
};

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
